import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    AddRatioToActKc2StrategyEnum,
    UerAddRatioToActKc2Mutation,
    UerAddRatioToWorkOrderFromPriceListMutation,
    WorkOrderPriceData,
    WorkOrderPriceItem,
} from 'gql/graphql';
import { RatioTypeEnum, WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { AddCoefficientTabsTypes } from 'pages/Project/UerProject/AddCoefficientTabsTypes';
import { CreateOrEditRatio } from 'pages/Project/UerProject/Ratio/CreateOrEditRatio/CreateOrEditRatio';
import { cnMixSpace } from 'uikit';
import { countTrueValues, getCheckedIdsArr } from 'utils';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import type { TextFieldPropValue } from '@consta/uikit/TextField';
import type { RowClassRules } from 'ag-grid-community';
import * as R from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { gqlAddRatioToActKC2, gqlAddRatioToWorkOrderFromPriceList } from '../workOrderQueries';

import { AddRatioButton } from './components/AddRatioButton';
import { RatioRowsWithScopesSelectorModal } from './components/RatioRowsWithScopesSelectorModal';
import { useColumnsAddCoefficient } from './hooks/useColumnsAddCoefficient';
import { useWorkOrderPriceListRatioData } from './hooks/useWorkOrderPriceListRatioData';

import './style/AddCoefficient.css';

export type TypeCheckedIds = {
    [keyof in RatioTypeEnum]: Record<string, boolean>;
};

export const getRowClass: RowClassRules = {
    groupRow: ({ data }: { data: { groupRow: boolean } }) => data?.groupRow,
};

const getRowId = ({ data }: { data: any }) => data?.id;

// @ts-ignore
export const listCheckedIds = R.pipe(R.values, R.mergeAll, getCheckedIdsArr);

export const replaceCyrillicLetters = (value: string) => Number(value.replace(/[ЁёА-я]/, ''));

export const uniqueValueArray = (array: [], propertyName: string) =>
    array.filter(
        (el1: any, index: number) => array.findIndex((a: any) => a[propertyName] === el1[propertyName]) === index,
    );

interface IAddCoefficientProps {
    workOrderStatus?: WorkOrderStatusEnum;
    uerId?: number;
    actKC2Id?: number;
    addStrategy?: AddRatioToActKc2StrategyEnum;
    closeModal?: () => void;
    onSuccess?: () => void;
    uer?: WorkOrderPriceData | WorkOrderPriceItem;
    contractId: number;
}

export const AddCoefficient = (props: IAddCoefficientProps) => {
    const {
        addStrategy: addStrategyProp,
        workOrderStatus,
        actKC2Id: actKC2IdProp,
        uerId: uerIdProp,
        uer: uerProp,
        closeModal,
        onSuccess,
        contractId,
    } = props;

    const {
        id: projectId,
        orderId,
        uerId: uerIdParam,
        actKC2Id: actKC2IdParam,
        addStrategy: strategyParam,
    } = useParams();

    const { uerId, actKC2Id, strategy } = useMemo(
        () => ({
            strategy: addStrategyProp ?? strategyParam,
            uerId: uerIdProp ? String(uerIdProp) : uerIdParam,
            actKC2Id: actKC2IdProp ? String(actKC2IdProp) : actKC2IdParam,
        }),
        [actKC2IdParam, actKC2IdProp, addStrategyProp, strategyParam, uerIdParam, uerIdProp],
    );

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state: stateLocation }: any = useLocation();
    const btnConfirmLabel =
        workOrderStatus === WorkOrderStatusEnum.InProgress ? 'Добавить в Акт' : 'Добавить в Наряд-Заказ';

    const [checkedIds, setCheckedIds] = useState<TypeCheckedIds>({
        [RatioTypeEnum.ConstraintCoef]: {},
        [RatioTypeEnum.TechPartCoef]: {},
        [RatioTypeEnum.DismantlingCoef]: {},
    });

    const [filters, setFilters] = useState({
        [RatioTypeEnum.ConstraintCoef]: {},
        [RatioTypeEnum.TechPartCoef]: {},
        [RatioTypeEnum.DismantlingCoef]: {},
    });

    const [valueNote, setValueNote] = useState<Record<string, string>>({});
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isCoefficientCreating, setIsCoefficientCreating] = useState(false);
    const [isCoefficientsSelecting, setIsCoefficientsSelecting] = useState(false);
    const defaultTabId = RatioTypeEnum.ConstraintCoef;
    const [activeTabId, setActiveTabId] = useState<RatioTypeEnum>(defaultTabId);

    const handleChangesValueNote = useCallback(
        (rowId: number) =>
            ({ value }: { value: TextFieldPropValue }) => {
                setValueNote((ids) => ({ ...ids, [rowId]: value }));
            },
        [],
    );

    const {
        request: coefficientsRequest,
        reset: coefficientsReset,
        data: coefficientsData,
        responseRatioType: coefficientsRatioType,
    } = useWorkOrderPriceListRatioData({
        filters,
        uerId: Number(uerId),
        ratioType: activeTabId,
    });

    // TODO: 22.02 изменили сигнатуру вызова
    const [
        addCoefficientResponse,
        addCoefficientResponseStatus,
        addCoefficientResponseError,
        addCoefficientToWorkOrder,
    ] = useRequest({
        requestId: 'gqlAddRatioToWorkOrderFromPriceList',
        query: (actKC2Id ? gqlAddRatioToActKC2 : gqlAddRatioToWorkOrderFromPriceList) as any,
        withEffect: false,
    });

    const response = useMemo(
        () =>
            actKC2Id
                ? (addCoefficientResponse as UerAddRatioToActKc2Mutation)?.uerAddRatioToActKC2
                : (addCoefficientResponse as UerAddRatioToWorkOrderFromPriceListMutation)
                      ?.uerAddRatioToWorkOrderFromPriceList,
        [actKC2Id, addCoefficientResponse],
    );

    useEffect(() => {
        if (isSubmitted && addCoefficientResponseStatus === RequestStatus.SUCCESS && response?.result) {
            if (onSuccess) {
                onSuccess();
                closeModal?.();
            } else {
                navigate('../', { replace: true, state: { reload: true } });
            }
        }
    }, [addCoefficientResponseStatus, closeModal, isSubmitted, navigate, onSuccess, response?.result]);

    const currentRowData = useMemo(() => {
        if (!coefficientsRatioType) {
            return null;
        }

        return coefficientsData[coefficientsRatioType];
    }, [coefficientsRatioType, coefficientsData]);

    const updateGroupRowData = useMemo(() => {
        if (!currentRowData || !Array.isArray(currentRowData)) {
            return [];
        }

        const hasNewObj = currentRowData.some((item) => item);

        if (hasNewObj && activeTabId !== RatioTypeEnum.TechPartCoef) {
            const rowGroup = {
                id: currentRowData[0].groupId,
                workConditions: currentRowData[0].groupName,
                ratioType: currentRowData[0].ratioType,
                groupRow: true,
            };

            return [rowGroup, ...currentRowData];
        }

        return currentRowData;
    }, [activeTabId, currentRowData]);

    useEffect(() => {
        if (!coefficientsRatioType) {
            return;
        }

        const currentTabData = coefficientsData[coefficientsRatioType];

        if (currentTabData === null) {
            return;
        }

        const rowsIds = currentTabData.map((r) => r.id);

        setCheckedIds((ids) => {
            const activeTabIds = ids[coefficientsRatioType];
            const activeKeys = Object.keys(activeTabIds).filter((k) => activeTabIds[k]);
            const checkedRowsIds = activeKeys.filter((id) => rowsIds.includes(Number(id)));

            return {
                ...ids,
                [coefficientsRatioType]: checkedRowsIds?.reduce((acc: any, id) => {
                    acc[id] = true;

                    return acc;
                }, {}),
            };
        });
    }, [coefficientsData, coefficientsRatioType]);

    const countCheckedItems: { [key: string]: number } = useMemo(
        () => R.map(countTrueValues, checkedIds),
        [checkedIds],
    );

    const handleAllChangeCheckbox = useCallback(
        (data: Record<string, boolean>) => () => {
            setCheckedIds((checkedId) => ({
                ...checkedId,
                [activeTabId]: data,
            }));
        },
        [activeTabId],
    );

    const handleChangeCheckbox = useCallback(
        (row: any) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedIds((ids) => ({ ...ids, [activeTabId]: { ...ids[activeTabId], [row.id]: checked } }));
            },
        [activeTabId],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilters((prevFilters) => ({
                ...prevFilters,
                [activeTabId]: {
                    ...prevFilters[activeTabId],
                    [fieldName]: checkedValues,
                },
            }));
        },
        [activeTabId],
    );

    const handleCloseModal = useCallback(() => {
        if (closeModal) {
            closeModal();
        } else if (!isCoefficientCreating) {
            coefficientsReset();
            navigate('../', { replace: true });
        }
    }, [closeModal, coefficientsReset, isCoefficientCreating, navigate]);

    const checkedListIds = useMemo(() => listCheckedIds(checkedIds), [checkedIds]);

    const ratioInputs = useMemo(() => {
        if (Array.isArray(checkedListIds)) {
            return checkedListIds.map((ratioRowId) => ({
                ratioRowId,
                rationale: valueNote[ratioRowId] || '',
            }));
        }

        return [];
    }, [checkedListIds, valueNote]);

    const handleModalConfirm = useCallback(() => {
        if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
            dispatch(
                addCoefficientToWorkOrder({
                    workOrderPriceId: Number(uerId),
                    ratioRowIds: listCheckedIds(checkedIds),
                    actKC2Id: actKC2Id && Number(actKC2Id),
                    strategy,
                }),
            );
        }
        if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
            dispatch(
                addCoefficientToWorkOrder({
                    workOrderPriceId: Number(uerId),
                    ratioInputs,
                    actKC2Id: actKC2Id && Number(actKC2Id),
                    strategy,
                }),
            );
        }

        sendGpnAnalyticsEvent({
            componentId: 'rim_NZ_add to order coef button_pressed',
            projectId,
            orderId: Number(orderId),
            orderStatus: workOrderStatus,
            addType: WorkOrderPriceTypeEnum.Coefficient,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        });

        setIsSubmitted(true);
    }, [
        actKC2Id,
        addCoefficientToWorkOrder,
        checkedIds,
        dispatch,
        orderId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        ratioInputs,
        sendGpnAnalyticsEvent,
        strategy,
        uerId,
        workOrderStatus,
    ]);

    const modalTitle = 'Добавление коэффициента к расценке';

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(response?.validation);
    }, [response]);

    const onCreateCoefficientVisibilityChange = useCallback(() => {
        setIsCoefficientCreating(!isCoefficientCreating);
    }, [isCoefficientCreating]);

    const onSelectCoefficientVisibilityChange = useCallback(() => {
        setIsCoefficientsSelecting(!isCoefficientsSelecting);
    }, [isCoefficientsSelecting]);

    const [resetFilterItems] = useResetStoreData({ requestId: 'uerWorkOrderPriceListRatio' });

    const onCreateCoefficientConfirm = useCallback(
        (id: number) => {
            onCreateCoefficientVisibilityChange();
            setCheckedIds((ids) => ({ ...checkedIds, [activeTabId]: { ...ids[activeTabId], [id]: true } }));
            coefficientsRequest();
            dispatch(resetFilterItems(undefined));
        },
        [activeTabId, checkedIds, coefficientsRequest, dispatch, onCreateCoefficientVisibilityChange, resetFilterItems],
    );

    const onCoefficientTypeChange = useCallback((coefficientType) => {
        setActiveTabId(coefficientType);
    }, []);

    const onCoefficientSelectingClose = useCallback(
        (selectedIds?: number[]) => {
            onSelectCoefficientVisibilityChange();

            if (selectedIds?.length) {
                const selectedObject = selectedIds.reduce(
                    (acc, id) => {
                        acc[id] = true;

                        return acc;
                    },
                    {} as Record<string, boolean>,
                );

                setCheckedIds((ids) => ({ ...checkedIds, [activeTabId]: { ...ids[activeTabId], ...selectedObject } }));
            }

            coefficientsRequest();
            dispatch(resetFilterItems(undefined));
        },
        [activeTabId, checkedIds, coefficientsRequest, dispatch, onSelectCoefficientVisibilityChange, resetFilterItems],
    );

    const isConfirmEnabled = useMemo(
        () =>
            Object.keys(checkedIds).reduce((acc, key) => {
                const tabChecked = checkedIds[key as RatioTypeEnum];

                return acc || !!Object.keys(tabChecked).filter((k) => tabChecked[k]).length;
            }, false),
        [checkedIds],
    );

    const currentTabData = useMemo(() => {
        if (!coefficientsRatioType) {
            return null;
        }

        return coefficientsData[coefficientsRatioType];
    }, [coefficientsData, coefficientsRatioType]);

    const { finalColumns } = useColumnsAddCoefficient({
        data: currentTabData,
        checkedIds,
        activeTabId,
        changeFilterHandler,
        workOrderStatus,
        handleChangesValueNote,
        valueNote,
        handleChangeCheckbox,
        handleAllChangeCheckbox,
        uerId: Number(uerId),
        ratioType: activeTabId,
    });

    return (
        <>
            <ModalDialog
                title={modalTitle}
                isOpen
                hasOverlay
                onClose={handleCloseModal}
                onConfirm={handleModalConfirm}
                btnConfirmLabel={btnConfirmLabel}
                btnConfirmDisabled={!isConfirmEnabled}
                btnCancelLabel="Отменить"
                withTitleCloseButton
                className={
                    workOrderStatus === WorkOrderStatusEnum.InProgress
                        ? 'modalDialogAddCoefficient inProgress'
                        : 'modalDialogAddCoefficient'
                }
                leftPanel={
                    <AddRatioButton
                        onCreateCoefficientVisibilityChange={onCreateCoefficientVisibilityChange}
                        onSelectCoefficientVisibilityChange={onSelectCoefficientVisibilityChange}
                        ratioType={activeTabId}
                        groupType={currentRowData?.[0]?.groupType}
                    />
                }
                isOnClickOutsideDisable={isCoefficientCreating || isCoefficientsSelecting}
            >
                <AddCoefficientTabsTypes
                    onChangeActiveTab={setActiveTabId}
                    countItems={countCheckedItems}
                    className={cnMixSpace({ p: 'xl' })}
                    view="bordered"
                />
                <div id="addCoefficient" className="ag-theme-alpine AddCoefficient">
                    <AgGridTable
                        getRowId={getRowId}
                        rowData={updateGroupRowData}
                        columnDefs={finalColumns}
                        rowClassRules={getRowClass}
                        rowSelection="multiple"
                        rowHeight={40}
                        key={activeTabId}
                        context={{
                            tableUniqueId: `addCoefficients_${activeTabId}`,
                        }}
                    />
                </div>
                <ErrorHandlingModal
                    titleText={modalTitle}
                    isSubmitted={isSubmitted}
                    statusRequest={addCoefficientResponseStatus}
                    dataRequest={response}
                    errorsRequest={addCoefficientResponseError}
                    handleCloseModalError={handleCloseModalError}
                    handleCreateReport={handleCreateReport}
                />
            </ModalDialog>
            {isCoefficientCreating && (
                <CreateOrEditRatio
                    ratioType={activeTabId}
                    onClose={onCreateCoefficientVisibilityChange}
                    onConfirm={onCreateCoefficientConfirm}
                    onRatioTypeChange={onCoefficientTypeChange}
                    uer={uerProp || stateLocation}
                    groupType={currentRowData?.[0]?.groupType}
                />
            )}
            {isCoefficientsSelecting && (
                <RatioRowsWithScopesSelectorModal
                    onClose={onCoefficientSelectingClose}
                    uerBaseRowId={(uerProp || stateLocation)?.entryId}
                    ratioType={activeTabId}
                    contractId={contractId}
                />
            )}
        </>
    );
};

AddCoefficient.displayName = 'AddCoefficient';
